import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import MultiStepWizardPage from '../components/Wizard/MultiStepWizardPage';
import CertificateTemplateSelectStep from '../components/Modal/steps/CertificateTemplateSelectStep';
import CustomerInfoStep from '../components/Modal/steps/CustomerInfoStep';
import CertificateDetailsStep from '../components/Modal/steps/CertificateDetailsStep';
import CertificateTemplateFillGroupStep from '../components/Modal/steps/CertificateTemplateFillGroupStep';
import CertificateTemplateFillSchemaScreenStep from '../components/Modal/steps/CertificateTemplateFillSchemaScreenStep';
import CertificateReviewStep from '../components/Modal/steps/CertificateReviewStep';
import { certificatesApi, clientsApi } from '../utils/api';
import { useToast } from '../contexts/ToastContext';
import { isFilledSignatureValue } from '../utils/certificateFieldValidation';

export default function CertificateWizard() {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const cancel = () => {
    navigate('/dashboard/certificates');
  };

  const getSchemaScreens = (data: any): any[] => {
    const tmpl = data?.selectedTemplate;
    const schema = tmpl?.schemaJson;
    const screens: any[] = Array.isArray(schema?.screens) ? schema.screens : [];
    return screens.filter((s) => String(s?.key || '').trim());
  };

  const getGroupNames = (data: any): string[] => {
    const tmpl = data?.selectedTemplate;
    const fields: any[] = Array.isArray(tmpl?.fields) ? tmpl.fields : [];

    const set = new Set<string>();
    for (const f of fields) {
      const g = String(f?.groupName || '').trim() || 'Ungrouped';
      set.add(g);
    }

    const arr = Array.from(set);
    arr.sort((a, b) => {
      if (a === 'Ungrouped' && b !== 'Ungrouped') return 1;
      if (b === 'Ungrouped' && a !== 'Ungrouped') return -1;
      return a.localeCompare(b);
    });
    return arr;
  };

  const groupStepValidation = (groupName: string) => (d: any) => {
    const tmpl = d?.selectedTemplate;
    const fields: any[] = Array.isArray(tmpl?.fields) ? tmpl.fields : [];
    const filled: Record<string, any> = d.filledData && typeof d.filledData === 'object' ? d.filledData : {};

    const g = String(groupName || '').trim() || 'Ungrouped';
    for (const f of fields) {
      const gn = String(f?.groupName || '').trim() || 'Ungrouped';
      if (gn !== g) continue;
      if (!f?.required) continue;

      const key = String(f?.pdfFieldName || '').trim();
      if (!key) continue;

      const ft = String(f?.fieldType || 'text');
      const v = filled[key];

      if (ft === 'checkbox') {
        if (v !== true) return false;
        continue;
      }

      if (ft === 'signature') {
        if (!isFilledSignatureValue(v)) return false;
        continue;
      }

      if (!String(v ?? '').trim()) return false;
    }

    return true;
  };

  const schemaScreenValidation = (screenKey: string) => (d: any) => {
    const tmpl = d?.selectedTemplate;
    const schema = tmpl?.schemaJson;
    const screens: any[] = Array.isArray(schema?.screens) ? schema.screens : [];
    const screen = screens.find((s) => String(s?.key || '').trim() === String(screenKey || '').trim());
    if (!screen) return true;

    const filled: Record<string, any> = d.filledData && typeof d.filledData === 'object' ? d.filledData : {};
    const sections: any[] = Array.isArray(screen?.sections) ? screen.sections : [];
    for (const sec of sections) {
      const fs: any[] = Array.isArray(sec?.fields) ? sec.fields : [];
      for (const f of fs) {
        if (!f?.required) continue;
        const key = String(f?.pdfFieldName || '').trim();
        if (!key) continue;
        const ft = String(f?.fieldType || 'text');
        const v = filled[key];
        if (ft === 'checkbox') {
          if (v !== true) return false;
        } else if (ft === 'signature') {
          if (!isFilledSignatureValue(v)) return false;
        } else {
          if (!String(v ?? '').trim()) return false;
        }
      }
    }
    return true;
  };

  const stepsFactory = useMemo(() => {
    return (data: any) => {
      const hasTemplate = Boolean(String(data?.templateId || '').trim());
      const screens = hasTemplate ? getSchemaScreens(data) : [];
      const groups = hasTemplate && screens.length === 0 ? getGroupNames(data) : [];

      const steps: any[] = [
        {
          id: 'template',
          title: 'Template',
          component: CertificateTemplateSelectStep,
          validation: (d: any) => Boolean(String(d.templateId || '').trim()),
        },
        {
          id: 'customer',
          title: 'Customer',
          component: CustomerInfoStep,
          validation: (d: any) => {
            if (d.isNewCustomer) {
              return Boolean(String(d.customerName || '').trim() && String(d.customerPhone || '').trim() && String(d.customerAddress || '').trim());
            }
            return Boolean(String(d.customerId || '').trim());
          },
        },
        {
          id: 'details',
          title: 'Details',
          component: CertificateDetailsStep,
          validation: (d: any) => {
            if (!d.type) return false;
            if (!d.issueDate) return false;
            if (!d.expiryDate) return false;
            return true;
          },
        },
      ];

      if (screens.length) {
        for (const s of screens) {
          const key = String(s?.key || '').trim();
          if (!key) continue;
          const title = String(s?.title || key);
          steps.push({
            id: `screen:${key}`,
            title,
            component: CertificateTemplateFillSchemaScreenStep,
            stepDataPatch: { activeScreenKey: key },
            validation: schemaScreenValidation(key),
          });
        }
      } else {
        for (const g of groups) {
          steps.push({
            id: `group:${g}`,
            title: g,
            component: CertificateTemplateFillGroupStep,
            stepDataPatch: { activeGroupName: g },
            validation: groupStepValidation(g),
          });
        }
      }

      steps.push({
        id: 'review',
        title: 'Review',
        component: CertificateReviewStep,
      });

      return steps;
    };
  }, []);

  return (
    <div className="page-container">
      <MultiStepWizardPage
        title="Create certificate"
        subtitle="Fill each section step-by-step, based on your template." 
        breadcrumb={
          <>
            Certificates <span className="mx-1 text-[#D1D5DB]">/</span> Create
          </>
        }
        onCancel={cancel}
        nextLabel="Continue"
        completeLabel="Create certificate"
        onComplete={async (data: any) => {
          let clientId = data.customerId;

          if (data.isNewCustomer) {
            const clientData = {
              name: data.customerName,
              email: data.customerEmail || '',
              phone: data.customerPhone,
              address: data.customerAddress,
              postcode: data.postcode || '',
              notes: data.notes || '',
            };

            const createdClient = await clientsApi.create(clientData);
            const clientPayload = createdClient.data;
            const client = clientPayload?.data || clientPayload;
            clientId = client?.id;
          }

          if (!clientId) throw new Error('Missing client');

          const payload: any = {
            type: data.type,
            clientId,
            jobId: String(data.jobId || '').trim() || undefined,
            siteId: String(data.siteId || '').trim() || undefined,
            templateId: String(data.templateId || '').trim() || undefined,
            filledData: data.filledData && typeof data.filledData === 'object' ? data.filledData : undefined,
            certificateNumber: (data.certificateNumber || '').trim() || undefined,
            issueDate: data.issueDate,
            expiryDate: data.expiryDate,
            notes:
              [String(data.notes || '').trim(), String(data.remedialActions || '').trim()].filter(Boolean).join('\n\n') || undefined,
          };

          const created = await certificatesApi.create(payload);
          const createdPayload = created.data;
          const certificate = createdPayload?.data || createdPayload;
          const certificateId = certificate?.id;

          if (!certificateId) throw new Error('Certificate creation failed');

          const generated = await certificatesApi.generatePdf(String(certificateId));
          const generatedPayload = generated.data;
          const result = generatedPayload?.data || generatedPayload;
          const url = result?.url;

          if (url) {
            window.open(url, '_blank', 'noopener,noreferrer');
          }

          showToast('Certificate created', 'success');
          cancel();
        }}
        steps={stepsFactory}
        initialData={{ filledData: {} }}
      />
    </div>
  );
}
